import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../icons';
import { Timestamp } from './StatusPieces';
import { Input } from '../primitives';

/**
 * AttentionFeed (CO-150): the one row pattern for "something needs a look" —
 * a live-site incident, a failed update, an infrastructure warning, a
 * readiness finding, an open monitoring incident. Each
 * row says what happened, where (project, environment, component), its
 * impact in a sentence, since when, and one useful action. Severity is an
 * icon, a word for assistive technology and a narrow accent on the row's
 * edge — never a red slab: a failed update is amber unless the failed
 * release itself serves.
 *
 * Signals folded under an item (the alert and the check behind an
 * incident) stay one disclosure away instead of being repeated in full.
 */

export interface FeedItem {
  key: string;
  severity: 'critical' | 'warning';
  title: string;
  /** Where: "cazane shop · Production". */
  context?: ReactNode;
  summary?: ReactNode;
  when?: { label: string; at: string };
  /** One action: a link to where it is fixed, or a button that does it here. */
  action?: { label: string; to?: string; onClick?: () => void; busy?: boolean };
  related?: { key: string; title: string; detail?: string }[];
  /** Advisory: offered for dismissal when the list allows it. */
  dismissible?: boolean;
}

export interface FeedSelection {
  selected: ReadonlySet<string>;
  onToggle: (key: string) => void;
}

const severityWord = { critical: 'Needs action now', warning: 'Needs attention' } as const;

export function AttentionFeed({
  items,
  label,
  limit,
  selection,
  testId,
}: {
  items: FeedItem[];
  /** Names the list for assistive technology. */
  label: string;
  /** Rows shown before "Show all"; all when omitted. */
  limit?: number;
  selection?: FeedSelection;
  testId?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = limit && !expanded ? items.slice(0, limit) : items;
  return (
    <>
      <ul className="feed" aria-label={label} data-testid={testId}>
        {shown.map((item) => (
          <FeedRow key={item.key} item={item} selection={selection} />
        ))}
      </ul>
      {limit && items.length > limit ? (
        <button type="button" className="link-button feed__more" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      ) : null}
    </>
  );
}

function FeedRow({ item, selection }: { item: FeedItem; selection?: FeedSelection }) {
  const related = item.related ?? [];
  return (
    <li className={`feed__item feed__item--${item.severity}`} data-severity={item.severity}>
      {selection ? (
        item.dismissible ? (
          <Input
            type="checkbox"
            className="feed__check"
            checked={selection.selected.has(item.key)}
            onChange={() => selection.onToggle(item.key)}
            aria-label={`Select “${item.title}” to dismiss`}
          />
        ) : (
          <span className="feed__check feed__check--none" title="An active problem: it clears by itself once its cause is fixed." aria-hidden="true" />
        )
      ) : null}
      <span className="feed__icon" aria-hidden="true">
        <Icon name={item.severity === 'critical' ? 'alert-circle' : 'alert-triangle'} size={16} />
      </span>
      <div className="feed__body">
        <p className="feed__title">
          <span>{item.title}</span>
          <span className="visually-hidden"> — {severityWord[item.severity]}.</span>
        </p>
        {item.context ? <p className="feed__context">{item.context}</p> : null}
        {item.summary ? <p className="feed__summary">{item.summary}</p> : null}
        {related.length > 0 ? (
          <details className="feed__related">
            <summary>
              {related.length === 1 ? '1 related signal' : `${related.length} related signals`}
            </summary>
            <ul>
              {related.map((signal) => (
                <li key={signal.key}>
                  {signal.title}
                  {signal.detail ? <span className="muted"> — {signal.detail}</span> : null}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
      <div className="feed__meta">
        {item.when ? (
          <span className="feed__when">
            <span className="feed__when-label">{item.when.label}</span>
            <Timestamp iso={item.when.at} />
          </span>
        ) : null}
        {item.action?.to ? (
          <Link className="button button--secondary button--small feed__action" to={item.action.to}>
            {item.action.label}
          </Link>
        ) : item.action?.onClick ? (
          <button type="button" className="button button--secondary button--small feed__action" onClick={item.action.onClick} disabled={item.action.busy}>
            {item.action.label}
          </button>
        ) : null}
      </div>
    </li>
  );
}
